import { ScheduleType } from 'src/contstant/commonLabels';
import { EndConditionType } from 'src/types/helios/AuditSchedule';
import { FormValidator, ValidationRules } from 'src/utils/FormValidator';

export const ScheduleValidationPaths = {
  NAME: {
    path: 'name',
    message: 'Error: Please enter the schedule name.',
  },
  DESCRIPTION: {
    path: 'description',
    message: 'Error: Please enter the schedule description.',
  },
  AUDIT_TYPE: {
    path: 'auditType',
    message: 'Error: Please select the audit type.',
  },
  TEMPLATE_ID: {
    path: 'templateId',
    message: 'Error: Please enter the template.',
  },
  SCHEDULE_TYPE: {
    path: 'scheduleType',
    message: 'Error: Please select the schedule type.',
  },
  ONE_TIME_DATE: {
    path: 'scheduleDetails.oneTime.date',
    message: 'Error: Schedule date is required for one-time schedules',
  },
  RECURRING_END_CONDITION_TYPE: {
    path: 'scheduleDetails.recurring.endCondition.type',
    message: 'Error: End condition for recurring schedules',
  },
  RECURRING_START_DATE: {
    path: 'scheduleDetails.recurring.startDate',
    message: 'Error: Start date is required for recurring schedules',
  },
  RECURRING_PATTERN_TYPE: {
    path: 'scheduleDetails.recurring.recurrencePattern.type',
    message: 'Error: Recurrence pattern type is required',
  },
  RECURRING_PATTERN_INTERVAL: {
    path: 'scheduleDetails.recurring.recurrencePattern.interval',
    message: 'Error: Recurrence interval must be greater than 0',
  },
  RECURRING_PATTERN_DAYS_OF_WEEK: {
    path: 'scheduleDetails.recurring.recurrencePattern.daysOfWeek',
    message: 'Error: At least one day of the week must be selected for weekly schedules',
  },
  RECURRING_PATTERN_DAY_OF_MONTH: {
    path: 'scheduleDetails.recurring.recurrencePattern.dayOfMonth',
    message: 'Error: Day of month must be between 1 and 31 for monthly schedules',
  },
  RECURRING_END_OCCURRENCES: {
    path: 'scheduleDetails.recurring.endCondition.occurrences',
    message: 'Error: Number of occurrences must be greater than 0',
  },
  RECURRING_END_DATE: {
    path: 'scheduleDetails.recurring.endCondition.endDate',
    message: 'Error: End date is required when ending on specific date',
  },
} as const;

export const AuditScheduleValidator = (): FormValidator => {
  const validator = new FormValidator();

  validator.addRule(
    ScheduleValidationPaths.NAME.path,
    ValidationRules.required,
    ScheduleValidationPaths.NAME.message,
  );
  // Description is now optional - no validation needed
  validator.addRule(
    ScheduleValidationPaths.AUDIT_TYPE.path,
    ValidationRules.required,
    ScheduleValidationPaths.AUDIT_TYPE.message,
  );
  validator.addRule(
    ScheduleValidationPaths.SCHEDULE_TYPE.path,
    ValidationRules.required,
    ScheduleValidationPaths.SCHEDULE_TYPE.message,
  );
  validator.addRule(
    ScheduleValidationPaths.TEMPLATE_ID.path,
    ValidationRules.required,
    ScheduleValidationPaths.TEMPLATE_ID.message,
  );

  // // One-time schedule validations
  validator.addRule(
    ScheduleValidationPaths.ONE_TIME_DATE.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.ONCE) {
        return allValues.scheduleDetails?.oneTime?.date ? true : false;
      }
      return true;
    }),
    ScheduleValidationPaths.ONE_TIME_DATE.message,
  );

  //End condition type validation for recurring schedules
  validator.addRule(
    ScheduleValidationPaths.RECURRING_END_CONDITION_TYPE.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        return allValues?.scheduleDetails?.recurring?.endCondition?.type ? true : false;
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_END_CONDITION_TYPE.message,
  );

  // // Recurring schedule validations
  validator.addRule(
    ScheduleValidationPaths.RECURRING_START_DATE.path,
    ValidationRules.custom((_: unknown, allValues) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        return allValues?.scheduleDetails?.recurring?.startDate ? true : false;
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_START_DATE.message,
  );
  validator.addRule(
    ScheduleValidationPaths.RECURRING_PATTERN_TYPE.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        return allValues?.scheduleDetails?.recurring?.recurrencePattern?.type
          ? true
          : false;
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_PATTERN_TYPE.message,
  );
  validator.addRule(
    ScheduleValidationPaths.RECURRING_PATTERN_INTERVAL.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        const interval =
          allValues?.scheduleDetails?.recurring?.recurrencePattern?.interval;
        return interval && interval > 0 ? true : false;
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_PATTERN_INTERVAL.message,
  );
  validator.addRule(
    ScheduleValidationPaths.RECURRING_PATTERN_DAYS_OF_WEEK.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        const pattern = allValues?.scheduleDetails?.recurring?.recurrencePattern;
        if (pattern?.type === 'WEEKLY') {
          return pattern.daysOfWeek && pattern.daysOfWeek.length > 0;
        }
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_PATTERN_DAYS_OF_WEEK.message,
  );
  validator.addRule(
    ScheduleValidationPaths.RECURRING_PATTERN_DAY_OF_MONTH.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        const pattern = allValues?.scheduleDetails?.recurring?.recurrencePattern;
        if (pattern?.type === 'MONTHLY') {
          const dayOfMonth = pattern.dayOfMonth;
          return dayOfMonth && dayOfMonth >= 1 && dayOfMonth <= 31;
        }
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_PATTERN_DAY_OF_MONTH.message,
  );

  validator.addRule(
    ScheduleValidationPaths.RECURRING_END_OCCURRENCES.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        const endCondition = allValues?.scheduleDetails?.recurring?.endCondition;
        if (endCondition?.type === EndConditionType.AFTER) {
          const occurrences = endCondition.occurrences;
          return occurrences && occurrences > 0;
        }
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_END_OCCURRENCES.message,
  );

  validator.addRule(
    ScheduleValidationPaths.RECURRING_END_DATE.path,
    ValidationRules.custom((_: unknown, allValues?) => {
      if (allValues?.scheduleType === ScheduleType.RECURRING) {
        const endCondition = allValues?.scheduleDetails?.recurring?.endCondition;
        if (endCondition?.type === EndConditionType.ON) {
          return endCondition.endDate ? true : false;
        }
      }
      return true;
    }),
    ScheduleValidationPaths.RECURRING_END_DATE.message,
  );
  return validator;
};
